import { layerLabel } from '@/utils/layerLabel';
import { useTranslation } from 'react-i18next';
import { localeTag } from '@/i18n/format';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { RoutingControls, DEFAULT_ROUTING, type RoutingChoice } from '../Info/RoutingControls';
import {
  SquareDashedBottom, Loader2, ChevronDown, ChevronLeft, ChevronRight,
  X, GripHorizontal, Copy, Play, Minus, Maximize2,
  Footprints, Car, Bike, Circle, CheckSquare, Square,
} from 'lucide-react';
import { useMapStore } from '../../store/map.store';
import { useLayerStore } from '../../store/layer.store';
import TileWMS from 'ol/source/TileWMS';
import ImageWMS from 'ol/source/ImageWMS';
import { cn } from '../../utils/cn';
import { useColumnFilters, FilterHeaderCell } from '../common/ColumnFilter';
import { useColumnTranslations, useLayerTranslations } from '../../hooks/useColumnTranslations';
import { getMapColors } from '../../themes/mapTheme';
import type {
  FeatureInfoResult,
  CatchmentSelection, CatchmentTimes, CatchmentOptions,
  LayerControl, TrafficControl,
} from '../Info/FeatureInfoPanel';

/**
 * Info by Polygon (polyInfo) — FeatureInfoPanel-style results.
 *
 * 1. User selects a polygon layer + report layers
 * 2. Clicks polygon on map → WFS BBOX query on selected layers
 * 3. Results displayed in tabbed tables with column filters
 * 4. Catchment can be launched from point features
 * 5. Numeric columns show SUM / AVG / MAX / MIN summary rows
 */

/* ══════════════════════════════════════════════════════════════
   Types
   ══════════════════════════════════════════════════════════════ */

interface PolyInfoPanelProps {
  onClose: () => void;
  clickedResults: FeatureInfoResult[] | null;
  clickedFeatureExtent: [number, number, number, number] | null;
  onStartCatchment?: (
    selections: CatchmentSelection[],
    times: CatchmentTimes,
    options: CatchmentOptions,
  ) => Promise<void>;
  isLoading?: boolean;
}

interface WfsFeature {
  id: string;
  properties: Record<string, any>;
  geometry?: { type: string; coordinates: any };
}

interface LayerResult {
  layerName: string;
  features: WfsFeature[];
  keys: string[];
}

/* ══════════════════════════════════════════════════════════════
   Helpers
   ══════════════════════════════════════════════════════════════ */

const EXCLUDED_KEYS = new Set([
  'geometry', 'geom', 'the_geom', 'wkb_geometry', 'bbox',
  'hashKey', 'featureId', 'ogc_fid', 'gid', 'objectid',
]);

function shouldHide(key: string): boolean {
  const k = key.toLowerCase();
  return EXCLUDED_KEYS.has(k) || k.endsWith('_geom') || k.endsWith('_shape');
}

function extractPointCoord(feat: WfsFeature): [number, number] | null {
  const g = feat.geometry;
  if (g?.type === 'Point' && Array.isArray(g.coordinates)) {
    return [g.coordinates[0], g.coordinates[1]];
  }
  const p = feat.properties;
  const lon = p.xCoordinate ?? p.longitude ?? p.lon ?? p.x;
  const lat = p.yCoordinate ?? p.latitude ?? p.lat ?? p.y;
  if (lon != null && lat != null) return [Number(lon), Number(lat)];
  return null;
}

function featureLabel(feat: WfsFeature, idx: number): string {
  const p = feat.properties;
  return (
    p.name || p.Name || p.NAME ||
    p.Store_Name || p.store_name ||
    p.Mall_Name || p.mall_name ||
    p.adi || p.Adi ||
    p.ilce_adi || p.il_adi ||
    `Feature ${idx + 1}`
  );
}

const PAGE_SIZE = 50;
const DEFAULT_WALKIN = ['5', '10', '15', '', ''];
const DEFAULT_DRIVE  = ['5', '15', '30', '45', ''];
const DEFAULT_MOTOR  = ['5', '15', '30', '45', ''];
const DEFAULT_RING   = ['0.25', '0.75', '1', '', ''];

/* ══════════════════════════════════════════════════════════════
   Drag / Resize
   ══════════════════════════════════════════════════════════════ */

function useDragResize(
  initialPos: { x: number; y: number },
  initialSize: { w: number; h: number },
  minSize = { w: 400, h: 300 },
) {
  const [pos, setPos] = useState(initialPos);
  const [size, setSize] = useState(initialSize);
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);
  const resizeRef = useRef<{ startX: number; startY: number; origW: number; origH: number } | null>(null);

  const onDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y };
    const onMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      setPos({
        x: dragRef.current.origX + ev.clientX - dragRef.current.startX,
        y: dragRef.current.origY + ev.clientY - dragRef.current.startY,
      });
    };
    const onUp = () => { dragRef.current = null; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [pos]);

  const onResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    resizeRef.current = { startX: e.clientX, startY: e.clientY, origW: size.w, origH: size.h };
    const onMove = (ev: MouseEvent) => {
      if (!resizeRef.current) return;
      setSize({
        w: Math.max(minSize.w, resizeRef.current.origW + ev.clientX - resizeRef.current.startX),
        h: Math.max(minSize.h, resizeRef.current.origH + ev.clientY - resizeRef.current.startY),
      });
    };
    const onUp = () => { resizeRef.current = null; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [size, minSize]);

  return { pos, size, onDragStart, onResizeStart };
}

/* ══════════════════════════════════════════════════════════════
   Sub-components
   ══════════════════════════════════════════════════════════════ */

const Pill: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={cn(
      'px-2 py-0.5 rounded text-[10px] font-medium transition-colors border',
      active
        ? 'bg-gn-blue-light border-gn-blue text-gn-blue'
        : 'bg-gn-secondary border-gn-light text-gn-secondary hover:text-gn-primary hover:border-gn',
    )}
  >
    {children}
  </button>
);

const TimeRow: React.FC<{
  icon: React.ReactNode; label: string; values: string[];
  onChange: (v: string[]) => void; unit: string;
}> = ({ icon, label, values, onChange, unit }) => (
  <div className="flex items-center gap-1.5">
    {icon}
    <span className="text-[10px] text-gn-primary w-10 shrink-0">{label}</span>
    {values.map((t, i) => (
      <input key={i} type="text" value={t}
        onChange={(e) => { const n = [...values]; n[i] = e.target.value; onChange(n); }}
        className="w-11 px-1 py-1 text-[11px] text-center text-gn-primary bg-gn-tertiary border border-gn rounded focus:border-gn-blue focus:outline-none"
        placeholder="0" />
    ))}
    <span className="text-[9px] text-gn-secondary">{unit}</span>
  </div>
);

const CheckIcon: React.FC<{ checked: boolean }> = ({ checked }) =>
  checked ? <CheckSquare size={13} className="text-gn-blue" /> : <Square size={13} className="text-gn-secondary" />;

/* ══════════════════════════════════════════════════════════════
   Numeric summary helpers
   ══════════════════════════════════════════════════════════════ */

interface NumericSummary {
  sum: number;
  avg: number;
  min: number;
  max: number;
  count: number;
}

function computeNumericSummaries(
  features: WfsFeature[],
  columns: string[],
): Record<string, NumericSummary> {
  const result: Record<string, NumericSummary> = {};
  for (const col of columns) {
    let sum = 0, min = Infinity, max = -Infinity, count = 0;
    for (const f of features) {
      const raw = f.properties[col];
      if (raw == null) continue;
      const val = typeof raw === 'number' ? raw : parseFloat(String(raw));
      if (isNaN(val)) continue;
      sum += val;
      if (val < min) min = val;
      if (val > max) max = val;
      count++;
    }
    if (count > 0) {
      result[col] = { sum, avg: sum / count, min, max, count };
    }
  }
  return result;
}

function isNumericColumn(features: WfsFeature[], col: string): boolean {
  let numCount = 0;
  let total = 0;
  for (const f of features) {
    const v = f.properties[col];
    if (v == null) continue;
    total++;
    if (typeof v === 'number' || (typeof v === 'string' && !isNaN(parseFloat(v)) && isFinite(Number(v)))) {
      numCount++;
    }
  }
  // Column is numeric if at least 80% of non-null values are numbers
  return total > 0 && numCount / total >= 0.8;
}

function formatNum(val: number): string {
  if (Number.isInteger(val)) return val.toLocaleString(localeTag());
  return val.toLocaleString(localeTag(), { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/* ══════════════════════════════════════════════════════════════
   Component
   ══════════════════════════════════════════════════════════════ */

export const PolyInfoPanel: React.FC<PolyInfoPanelProps> = ({
  onClose,
  clickedResults: _clickedResults,
  clickedFeatureExtent,
  onStartCatchment,
  isLoading = false,
}) => {
  const { t, i18n } = useTranslation();
  /* ── Column & layer translations ── */
  const { t: tCol } = useColumnTranslations();
  const { t: tLayer } = useLayerTranslations();

  const allLayers = useLayerStore((s) => s.allLayers);
  const visibility = useLayerStore((s) => s.visibility);
  const mapInstance = useMapStore((s) => s.mapInstance);

  const { pos, size, onDragStart, onResizeStart } = useDragResize(
    { x: Math.max(12, window.innerWidth - 780), y: 12 },
    { w: Math.min(window.innerWidth * 0.9, 760), h: Math.min(window.innerHeight * 0.75, 580) },
  );

  /* ── Panel state ── */
  const [minimized, setMinimized] = useState(false);
  const [phase, setPhase] = useState<'config' | 'results'>('config');

  /* ── Config state ── */
  const [polyLayer, setPolyLayer] = useState('');
  const [reportLayers, setReportLayers] = useState<Record<string, boolean>>({});
  const [useVisibleOnly, setUseVisibleOnly] = useState(true);

  /* ── Results state ── */
  const [results, setResults] = useState<LayerResult[]>([]);
  const [queryLoading, setQueryLoading] = useState(false);
  const [activeLayerIdx, setActiveLayerIdx] = useState(0);
  const [pages, setPages] = useState<Record<number, number>>({});

  /* ── Catchment state ── */
  const [catchSel, setCatchSel] = useState<Record<string, { walkin: boolean; drive: boolean; motor: boolean; ring: boolean }>>({});
  const [walkinTimes, setWalkinTimes] = useState(DEFAULT_WALKIN);
  const [driveTimes, setDriveTimes]   = useState(DEFAULT_DRIVE);
  const [motorTimes, setMotorTimes]   = useState(DEFAULT_MOTOR);
  const [ringDists, setRingDists]     = useState(DEFAULT_RING);
  const [layerControl, setLayerControl]     = useState<LayerControl>('nbHood');
  const [trafficControl, setTrafficControl] = useState<TrafficControl>('hvytrf');
  const [routing, setRouting] = useState<RoutingChoice>(DEFAULT_ROUTING);

  /* ── Polygon layer options ── */
  const polygonLayerOptions = useMemo(() => {
    const opts: { name: string; label: string }[] = [];
    if (mapInstance) {
      mapInstance.getLayers().forEach((layer: any) => {
        const src = layer.getSource?.();
        if (src && (src instanceof TileWMS || src instanceof ImageWMS) && layer.getVisible()) {
          const name = layer.get('name');
          if (name) {
            const db = allLayers.find((l) => l.layerName === layer.get('layerName'));
            opts.push({ name, label: db ? layerLabel(db) : name });
          }
        }
      });
    }
    return opts;
  }, [mapInstance, allLayers, i18n.language]);

  /* ── Report layer options ── */
  const reportLayerOptions = useMemo(() => {
    return allLayers.filter((l) => l.layerName).map((l) => ({
      id: l.id,
      name: l.layerName,
      label: layerLabel(l),
      visible: !!visibility[l.id],
    }));
  }, [allLayers, visibility, i18n.language]);

  const toggleReportLayer = (name: string) => {
    setReportLayers((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  /* ── Auto-query on polygon click ── */
  useEffect(() => {
    if (clickedFeatureExtent && polyLayer) {
      runQuery(clickedFeatureExtent);
    }
  }, [clickedFeatureExtent, polyLayer]);

  /* ── WFS query ── */
  const runQuery = useCallback(async (extent: [number, number, number, number]) => {
    if (!mapInstance) return;
    setQueryLoading(true);
    setResults([]);

    const [minLon, minLat, maxLon, maxLat] = extent;
    if (minLon === 0 && minLat === 0 && maxLon === 0 && maxLat === 0) {
      setQueryLoading(false);
      return;
    }

    let layersToQuery: { name: string; wfsUrl: string; typeName: string }[] = [];

    const extractLayerInfo = (layer: any): { name: string; wfsUrl: string; typeName: string } | null => {
      const src = layer.getSource?.();
      if (!src || (!(src instanceof TileWMS) && !(src instanceof ImageWMS))) return null;
      const name = layer.get('name');
      if (!name || name === polyLayer) return null;
      const wmsUrl: string = (src as any).getUrls?.()[0] || (src as any).getUrl?.() || '';
      if (!wmsUrl) return null;
      const wfsUrl = wmsUrl.replace(/\/wms\/?$/i, '/ows').replace(/\/wms\?/i, '/ows?');
      const params = src.getParams?.() || {};
      const layersParam: string = params.LAYERS || name;
      return { name, wfsUrl, typeName: layersParam };
    };

    if (useVisibleOnly) {
      mapInstance.getLayers().forEach((layer: any) => {
        if (!layer.getVisible()) return;
        const info = extractLayerInfo(layer);
        if (info) layersToQuery.push(info);
      });
    } else {
      const checkedNames = Object.entries(reportLayers)
        .filter(([_, checked]) => checked)
        .map(([name]) => name);
      mapInstance.getLayers().forEach((layer: any) => {
        const info = extractLayerInfo(layer);
        if (info && checkedNames.includes(info.name)) layersToQuery.push(info);
      });
    }

    layersToQuery = layersToQuery.filter((l) => !l.name.toLowerCase().includes('label'));

    const allResults: LayerResult[] = [];
    const promises: Promise<void>[] = [];

    for (const { name, wfsUrl, typeName } of layersToQuery) {
      const bboxStr = `${minLon.toFixed(6)},${minLat.toFixed(6)},${maxLon.toFixed(6)},${maxLat.toFixed(6)},EPSG:4326`;
      const params = new URLSearchParams({
        service: 'WFS',
        version: '1.1.0',
        request: 'GetFeature',
        typeName,
        // EPSG:4326 (not the urn form): GeoServer then reads the box and writes the GeoJSON as lon,lat.
        srsName: 'EPSG:4326',
        outputFormat: 'application/json',
        bbox: bboxStr,
        maxFeatures: '50000',
      });

      const p = fetch(`${wfsUrl}?${params.toString()}`)
        .then((r) => r.json())
        .then((data) => {
          if (data?.features?.length) {
            const keys = Object.keys(data.features[0].properties || {}).filter(
              (k) => !shouldHide(k),
            );
            const features: WfsFeature[] = data.features.map((f: any) => ({
              id: f.id || '',
              properties: f.properties || {},
              geometry: f.geometry || undefined,
            }));
            allResults.push({ layerName: name, features, keys });
          }
        })
        .catch((err) => console.warn(`WFS query failed for ${name}:`, err));

      promises.push(p);
    }

    await Promise.all(promises);
    setResults(allResults);
    if (allResults.length > 0) {
      setActiveLayerIdx(0);
      setPhase('results');
    }
    setQueryLoading(false);
  }, [mapInstance, polyLayer, useVisibleOnly, reportLayers]);

  /* ── Active layer & columns ── */
  const activeLayer = results[activeLayerIdx] || results[0];
  const page = pages[activeLayerIdx] || 0;

  const columns = useMemo(() => {
    if (!activeLayer) return [];
    const keySet = new Set<string>();
    activeLayer.features.forEach((f) => {
      Object.keys(f.properties).forEach((k) => { if (!shouldHide(k)) keySet.add(k); });
    });
    return Array.from(keySet);
  }, [activeLayer]);

  /* ── Column filtering ── */
  const propertiesList = useMemo(
    () => (activeLayer?.features ?? []).map((f) => f.properties),
    [activeLayer],
  );
  const { filters, setFilter, clearFilters, hasActiveFilters, filteredFeatures: filteredProps, stringColumns } =
    useColumnFilters(propertiesList, columns);

  const filteredWithIndex = useMemo(() => {
    if (!activeLayer) return [];
    const propSet = new Set(filteredProps);
    return activeLayer.features
      .map((f, idx) => ({ feature: f, originalIdx: idx }))
      .filter((item) => propSet.has(item.feature.properties));
  }, [activeLayer, filteredProps]);

  const totalPages = Math.ceil(filteredWithIndex.length / PAGE_SIZE);
  const pageItems = useMemo(
    () => filteredWithIndex.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [filteredWithIndex, page],
  );

  useEffect(() => {
    setPages((prev) => ({ ...prev, [activeLayerIdx]: 0 }));
  }, [filters, activeLayerIdx]);

  /* ── Numeric summaries (computed on ALL features, not filtered) ── */
  const numericCols = useMemo(() => {
    if (!activeLayer) return new Set<string>();
    const cols = new Set<string>();
    for (const col of columns) {
      if (isNumericColumn(activeLayer.features, col)) cols.add(col);
    }
    return cols;
  }, [activeLayer, columns]);

  const summaries = useMemo(() => {
    if (!activeLayer) return {};
    return computeNumericSummaries(activeLayer.features, Array.from(numericCols));
  }, [activeLayer, numericCols]);

  const hasAnySummary = Object.keys(summaries).length > 0;

  /* ── Catchment helpers ── */
  const hasCatchableFeatures = useMemo(
    () => results.some((lr) => lr.features.some((f) => extractPointCoord(f) !== null)),
    [results],
  );

  const selectedCount = useMemo(
    () => Object.values(catchSel).filter((s) => s.walkin || s.drive || s.motor || s.ring).length,
    [catchSel],
  );

  const activeTypes = useMemo(() => {
    const t = { walkin: false, drive: false, motor: false, ring: false };
    Object.values(catchSel).forEach((s) => {
      if (s.walkin) t.walkin = true;
      if (s.drive) t.drive = true;
      if (s.motor) t.motor = true;
      if (s.ring) t.ring = true;
    });
    return t;
  }, [catchSel]);

  const showConfig = activeTypes.walkin || activeTypes.drive || activeTypes.motor || activeTypes.ring;

  const toggleCatch = useCallback(
    (key: string, type: 'walkin' | 'drive' | 'motor' | 'ring') => {
      setCatchSel((prev) => {
        const cur = prev[key] || { walkin: false, drive: false, motor: false, ring: false };
        return { ...prev, [key]: { ...cur, [type]: !cur[type] } };
      });
    }, [],
  );

  const toggleAllOnPage = useCallback(
    (type: 'walkin' | 'drive' | 'motor' | 'ring') => {
      setCatchSel((prev) => {
        const next = { ...prev };
        const pts = pageItems.filter(({ feature }) => extractPointCoord(feature) !== null);
        const allTrue = pts.every(({ originalIdx }) => {
          const k = `${activeLayerIdx}-${originalIdx}`;
          return next[k]?.[type];
        });
        pts.forEach(({ originalIdx }) => {
          const k = `${activeLayerIdx}-${originalIdx}`;
          const cur = next[k] || { walkin: false, drive: false, motor: false, ring: false };
          next[k] = { ...cur, [type]: !allTrue };
        });
        return next;
      });
    }, [pageItems, activeLayerIdx],
  );

  const handleStartCatchment = useCallback(() => {
    const selections: CatchmentSelection[] = [];
    Object.entries(catchSel).forEach(([key, sel]) => {
      if (!sel.walkin && !sel.drive && !sel.motor && !sel.ring) return;
      const [liStr, fiStr] = key.split('-');
      const lr = results[parseInt(liStr)];
      if (!lr) return;
      const feat = lr.features[parseInt(fiStr)];
      if (!feat) return;
      const coord = extractPointCoord(feat);
      if (!coord) return;
      selections.push({
        name: featureLabel(feat, parseInt(fiStr)),
        lon: coord[0], lat: coord[1],
        walkin: sel.walkin, drive: sel.drive, motor: sel.motor, ringBuffer: sel.ring,
      });
    });
    if (selections.length === 0) return;

    const times: CatchmentTimes = {
      walkin: walkinTimes.map(Number),
      drive:  driveTimes.map(Number),
      motor:  motorTimes.map(Number),
      ring:   ringDists.map(Number),
    };
    const options: CatchmentOptions = { layerControl, trafficControl, routing };
    onStartCatchment?.(selections, times, options);
  }, [catchSel, results, walkinTimes, driveTimes, motorTimes, ringDists, layerControl, trafficControl, routing, onStartCatchment]);

  const setPage = (p: number) => setPages((prev) => ({ ...prev, [activeLayerIdx]: p }));

  const totalFeatures = results.reduce((s, r) => s + r.features.length, 0);

  /* ── Minimized pill ── */
  if (minimized) {
    return (
      <button
        onClick={() => setMinimized(false)}
        className="fixed z-50 flex items-center gap-2 px-3 py-2 rounded-lg bg-gn-primary border border-gn shadow-xl backdrop-blur-sm text-gn-primary hover:bg-gn-secondary transition-colors"
        style={{ left: pos.x, top: pos.y }}
      >
        <Maximize2 size={14} className="text-gn-blue" />
        <span className="text-[11px] font-medium">Polygon Info ({totalFeatures})</span>
      </button>
    );
  }

  /* ══════════════════════════════════════════════════════════════
     Render
     ══════════════════════════════════════════════════════════════ */

  return (
    <>
      {/* Loading overlay */}
      {isLoading && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4 bg-gn-primary border border-gn rounded-xl px-8 py-6 shadow-2xl">
            <Loader2 size={36} className="text-gn-blue animate-spin" />
            <div className="text-center">
              <p className="text-gn-primary font-medium text-sm">{t('analysis.started', { title: t('analysis.catchment') })}</p>
              <p className="text-gn-secondary text-[11px] mt-1">{t('analysis.analyzing')}</p>
            </div>
          </div>
        </div>
      )}

      <div
        className="fixed z-50 flex flex-col rounded-lg shadow-2xl overflow-hidden"
        style={{
          left: pos.x, top: pos.y, width: size.w, height: size.h,
          backgroundColor: 'var(--gn-glass-bg)',
          border: '1px solid var(--gn-border)',
          backdropFilter: 'blur(12px)',
        }}
      >
        {/* ─── Header ─── */}
        <div
          className="flex items-center justify-between px-3 py-2 shrink-0 cursor-move select-none"
          style={{ backgroundColor: 'var(--gn-glass-bg)', borderBottom: '1px solid var(--gn-border)' }}
          onMouseDown={onDragStart}
        >
          <div className="flex items-center gap-2">
            <GripHorizontal size={14} className="text-gn-secondary" />
            <div>
              <h3 className="font-semibold text-sm text-gn-primary flex items-center gap-1.5">
                <SquareDashedBottom size={14} className="text-gn-blue" />
                {t('tools.info_by_polygon')}
              </h3>
              {phase === 'results' && (
                <p className="text-[10px] text-gn-secondary">
                  {t('multi.summary', { features: totalFeatures, layers: results.length })}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {phase === 'results' && (
              <button onClick={() => setPhase('config')} className="px-2 py-1 text-[10px] text-gn-blue hover:bg-gn-tertiary rounded transition-colors" title={t('poly.back_to_config')}>
                {t('poly.config')}
              </button>
            )}
            <button onClick={() => setMinimized(true)} className="p-1 hover:bg-gn-tertiary rounded text-gn-secondary hover:text-gn-primary transition-colors" title={t('report.minimize')}>
              <Minus size={16} />
            </button>
            <button onClick={onClose} className="p-1 hover:bg-gn-tertiary rounded text-gn-secondary hover:text-gn-primary transition-colors">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* ═══ CONFIG PHASE ═══ */}
        {phase === 'config' && (
          <div className="flex-1 overflow-auto p-3 space-y-3">
            {/* Polygon layer */}
            <div>
              <span className="text-[10px] text-gn-secondary font-medium block mb-1">{t('poly.polygon_layer')}</span>
              <select
                value={polyLayer}
                onChange={(e) => setPolyLayer(e.target.value)}
                className="w-full px-2 py-1.5 text-xs text-gn-primary bg-gn-tertiary border border-gn rounded focus:border-gn-blue focus:outline-none"
              >
                <option value="">{t('poly.select_layer')}</option>
                {polygonLayerOptions.map((o) => (
                  <option key={o.name} value={o.name}>{o.label}</option>
                ))}
              </select>
            </div>

            {/* Report layers */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-gn-secondary font-medium">{t('poly.report_layers')}</span>
                <label className="flex items-center gap-1 text-[10px] text-gn-secondary">
                  <input type="checkbox" checked={useVisibleOnly} onChange={(e) => setUseVisibleOnly(e.target.checked)}
                    className="rounded accent-blue-500" />
                  {t('poly.visible_only')}
                </label>
              </div>
              {!useVisibleOnly && (
                <div className="max-h-28 overflow-y-auto space-y-0.5 border border-gn-light rounded p-1.5">
                  {reportLayerOptions.map((l) => (
                    <label key={l.name} className="flex items-center gap-1.5 text-[10px] text-gn-primary hover:bg-gn-tertiary px-1 rounded cursor-pointer">
                      <input type="checkbox" checked={!!reportLayers[l.name]} onChange={() => toggleReportLayer(l.name)}
                        className="rounded accent-blue-500" />
                      {l.label}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Instructions */}
            {!clickedFeatureExtent && polyLayer && (
              <div className="text-[11px] text-gn-warning bg-gn-warning-light border border-gn rounded px-2 py-1.5">
                {t('poly.click_polygon', { layer: polyLayer })}
              </div>
            )}
            {!polyLayer && (
              <div className="text-[11px] text-gn-warning bg-gn-warning-light border border-gn rounded px-2 py-1.5">
                {t('poly.need_layer')}
              </div>
            )}

            {queryLoading && (
              <div className="flex items-center gap-2 text-xs text-gn-blue">
                <Loader2 size={14} className="animate-spin" /> {t('poly.querying')}
              </div>
            )}

            {/* Show button to go to results if we already have them */}
            {results.length > 0 && !queryLoading && (
              <button
                onClick={() => setPhase('results')}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded text-xs font-medium bg-gn-blue text-gn-inverse hover:bg-gn-blue transition-colors"
              >
                {t('poly.view_results', { count: totalFeatures })}
              </button>
            )}
          </div>
        )}

        {/* ═══ RESULTS PHASE ═══ */}
        {phase === 'results' && results.length > 0 && (
          <>
            {/* Layer selector */}
            {results.length > 1 && (
              <div className="flex items-center gap-2 px-3 py-1.5 shrink-0" style={{ borderBottom: '1px solid var(--gn-border)' }}>
                <span className="text-[10px] text-gn-secondary shrink-0">{t('style.layer')}:</span>
                <div className="relative flex-1">
                  <select
                    value={activeLayerIdx}
                    onChange={(e) => setActiveLayerIdx(Number(e.target.value))}
                    className="w-full appearance-none bg-gn-secondary border border-gn rounded px-2.5 py-1 pr-7 text-[11px] text-gn-primary focus:border-gn-blue focus:outline-none cursor-pointer"
                  >
                    {results.map((lr, idx) => (
                      <option key={lr.layerName} value={idx}>
                        {tLayer(lr.layerName)} ({lr.features.length})
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-gn-secondary pointer-events-none" />
                </div>
              </div>
            )}

            {/* Catchment config */}
            {showConfig && (
              <div className="shrink-0 px-3 py-2 space-y-2" style={{ backgroundColor: 'var(--gn-bg-tertiary)', borderBottom: '1px solid var(--gn-border)' }}>
                <span className="text-[10px] text-gn-blue font-semibold">{t('info.catchment_config')}</span>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  <div className="flex items-center gap-1">
                    <span className="text-[9px] text-gn-secondary mr-0.5">{t('analysis.layers')}:</span>
                    <Pill active={layerControl === 'nbHood'} onClick={() => setLayerControl('nbHood')}>{t('analysis.layer_nbhood')}</Pill>
                    <Pill active={layerControl === 'allVisible'} onClick={() => setLayerControl('allVisible')}>{t('analysis.layer_all')}</Pill>
                    <Pill active={layerControl === 'cosmos'} onClick={() => setLayerControl('cosmos')}>{t('analysis.layer_cosmos')}</Pill>
                  </div>
                  <RoutingControls traffic={trafficControl} onTraffic={setTrafficControl} routing={routing} onRouting={setRouting} showTraffic={activeTypes.drive} />
                </div>
                {activeTypes.walkin && (
                  <TimeRow icon={<Footprints size={12} style={{ color: getMapColors().walk[0] }} />} label={t('info.walk')} values={walkinTimes} onChange={setWalkinTimes} unit={t('analysis.unit_min')} />
                )}
                {activeTypes.drive && (
                  <TimeRow icon={<Car size={12} style={{ color: getMapColors().drive[0] }} />} label={t('info.drive')} values={driveTimes} onChange={setDriveTimes} unit={t('analysis.unit_min')} />
                )}
                {activeTypes.motor && (
                  <TimeRow icon={<Bike size={12} style={{ color: getMapColors().motor[0] }} />} label={t('info.motor')} values={motorTimes} onChange={setMotorTimes} unit={t('analysis.unit_min')} />
                )}
                {activeTypes.ring && (
                  <TimeRow icon={<Circle size={12} style={{ color: getMapColors().ringBuffer }} />} label={t('analysis.ring')} values={ringDists} onChange={setRingDists} unit={t('analysis.unit_km')} />
                )}
              </div>
            )}

            {/* Data table */}
            <div className="flex-1 overflow-auto">
              <table className="w-full text-[11px] border-collapse">
                <thead>
                  <tr>
                    {hasCatchableFeatures && (
                      <>
                        <th className="sticky top-0 z-10 px-1 py-1.5 text-center cursor-pointer hover:bg-gn-tertiary" style={{ backgroundColor: 'rgba(31, 41, 55, 0.95)' }} title={t('info.select_all_walk')} onClick={() => toggleAllOnPage('walkin')}>
                          <Footprints size={12} className="mx-auto" style={{ color: getMapColors().walk[0] }} />
                        </th>
                        <th className="sticky top-0 z-10 px-1 py-1.5 text-center cursor-pointer hover:bg-gn-tertiary" style={{ backgroundColor: 'rgba(31, 41, 55, 0.95)' }} title={t('info.select_all_drive')} onClick={() => toggleAllOnPage('drive')}>
                          <Car size={12} className="mx-auto" style={{ color: getMapColors().drive[0] }} />
                        </th>
                        <th className="sticky top-0 z-10 px-1 py-1.5 text-center cursor-pointer hover:bg-gn-tertiary" style={{ backgroundColor: 'rgba(31, 41, 55, 0.95)' }} title={t('info.select_all_motor')} onClick={() => toggleAllOnPage('motor')}>
                          <Bike size={12} className="mx-auto" style={{ color: getMapColors().motor[0] }} />
                        </th>
                        <th className="sticky top-0 z-10 px-1 py-1.5 text-center cursor-pointer hover:bg-gn-tertiary" style={{ backgroundColor: 'rgba(31, 41, 55, 0.95)' }} title={t('info.select_all_ring')} onClick={() => toggleAllOnPage('ring')}>
                          <Circle size={12} className="mx-auto" style={{ color: getMapColors().ringBuffer }} />
                        </th>
                      </>
                    )}
                    {columns.map((col) => (
                      <FilterHeaderCell
                        key={col}
                        columnName={col}
                        isFilterable={stringColumns.has(col)}
                        filterValue={filters[col] || ''}
                        onFilterChange={setFilter}
                        className="sticky top-0 z-10 px-2 py-1.5 text-left text-gn-secondary font-semibold whitespace-nowrap bg-gn-secondary"
                      >
                        {tCol(col)}
                      </FilterHeaderCell>
                    ))}
                    <th className="sticky top-0 z-10 px-1 py-1.5" style={{ backgroundColor: 'rgba(31, 41, 55, 0.95)', borderBottom: '1px solid var(--gn-border)' }} />
                  </tr>
                </thead>
                <tbody>
                  {/* Summary rows */}
                  {hasAnySummary && (
                    <>
                      {(['sum', 'avg', 'max', 'min'] as const).map((stat) => (
                        <tr key={stat} className="bg-gn-tertiary" style={{ borderBottom: '1px solid var(--gn-border)' }}>
                          {hasCatchableFeatures && <td colSpan={4} />}
                          {columns.map((col) => (
                            <td key={col} className="px-2 py-1 text-[10px] whitespace-nowrap font-medium">
                              {summaries[col] ? (
                                <span className="text-gn-warning">
                                  <span className="text-gn-secondary uppercase text-[9px] mr-1">{stat}</span>
                                  {formatNum(summaries[col][stat])}
                                </span>
                              ) : (
                                stat === 'sum' ? <span className="text-gn-muted uppercase text-[9px]">{stat}</span> : null
                              )}
                            </td>
                          ))}
                          <td />
                        </tr>
                      ))}
                      <tr style={{ borderBottom: '2px solid rgba(55, 65, 81, 0.6)' }}>
                        <td colSpan={columns.length + (hasCatchableFeatures ? 5 : 1)} />
                      </tr>
                    </>
                  )}

                  {/* Data rows */}
                  {pageItems.map(({ feature: feat, originalIdx }) => {
                    const selKey = `${activeLayerIdx}-${originalIdx}`;
                    const sel = catchSel[selKey] || { walkin: false, drive: false, motor: false, ring: false };
                    const isPoint = extractPointCoord(feat) !== null;
                    const isSelected = sel.walkin || sel.drive || sel.motor || sel.ring;

                    return (
                      <tr key={originalIdx}
                        className={cn('transition-colors', isSelected ? 'bg-gn-blue-light' : 'hover:bg-gn-tertiary')}
                        style={{ borderBottom: '1px solid rgba(55, 65, 81, 0.25)' }}>
                        {hasCatchableFeatures && (
                          <>
                            <td className="px-1 py-1 text-center">
                              {isPoint ? <button onClick={() => toggleCatch(selKey, 'walkin')} className="hover:scale-110 transition-transform"><CheckIcon checked={sel.walkin} /></button> : <span className="text-gn-secondary">-</span>}
                            </td>
                            <td className="px-1 py-1 text-center">
                              {isPoint ? <button onClick={() => toggleCatch(selKey, 'drive')} className="hover:scale-110 transition-transform"><CheckIcon checked={sel.drive} /></button> : <span className="text-gn-secondary">-</span>}
                            </td>
                            <td className="px-1 py-1 text-center">
                              {isPoint ? <button onClick={() => toggleCatch(selKey, 'motor')} className="hover:scale-110 transition-transform"><CheckIcon checked={sel.motor} /></button> : <span className="text-gn-secondary">-</span>}
                            </td>
                            <td className="px-1 py-1 text-center">
                              {isPoint ? <button onClick={() => toggleCatch(selKey, 'ring')} className="hover:scale-110 transition-transform"><CheckIcon checked={sel.ring} /></button> : <span className="text-gn-secondary">-</span>}
                            </td>
                          </>
                        )}
                        {columns.map((col) => {
                          const val = feat.properties[col];
                          return (
                            <td key={col} className="px-2 py-1 text-gn-primary whitespace-nowrap max-w-[200px] truncate" title={val != null ? String(val) : ''}>
                              {val == null ? <span className="text-gn-muted italic">—</span>
                                : typeof val === 'number' ? val.toLocaleString(localeTag())
                                : String(val)}
                            </td>
                          );
                        })}
                        <td className="px-1 py-1">
                          <button onClick={() => navigator.clipboard.writeText(JSON.stringify(feat.properties, null, 2))}
                            className="text-gn-muted hover:text-gn-blue transition-colors" title={t('info.copy_row')}>
                            <Copy size={11} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {pageItems.length === 0 && (
                    <tr>
                      <td colSpan={columns.length + (hasCatchableFeatures ? 5 : 1)} className="px-4 py-6 text-center text-gn-secondary italic">
                        {hasActiveFilters ? t('info.no_match') : t('info.none')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-3 py-2 shrink-0" style={{ backgroundColor: 'var(--gn-glass-bg)', borderTop: '1px solid var(--gn-border)' }}>
              <div className="flex items-center gap-1">
                <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}
                  className="p-1 rounded hover:bg-gn-tertiary disabled:opacity-30 text-gn-secondary"><ChevronLeft size={14} /></button>
                <span className="text-[10px] text-gn-secondary">{page + 1} / {totalPages || 1}</span>
                <button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}
                  className="p-1 rounded hover:bg-gn-tertiary disabled:opacity-30 text-gn-secondary"><ChevronRight size={14} /></button>
                <span className="text-[10px] text-gn-muted ml-1">
                  {hasActiveFilters
                    ? `(${filteredWithIndex.length} / ${activeLayer?.features.length || 0})`
                    : `(${t('table.rows', { count: activeLayer?.features.length || 0 })})`}
                </span>
                {hasActiveFilters && (
                  <button onClick={clearFilters} className="ml-1 text-[9px] text-gn-blue hover:text-gn-blue">
                    {t('table.clear_filters')}
                  </button>
                )}
              </div>

              {hasCatchableFeatures && selectedCount > 0 && (
                <button
                  onClick={handleStartCatchment}
                  disabled={isLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded transition-colors shadow-sm text-gn-primary disabled:opacity-50"
                  style={{ backgroundColor: 'var(--gn-primary)' }}
                  onMouseEnter={(e) => { if (!isLoading) e.currentTarget.style.backgroundColor = 'var(--gn-primary-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--gn-primary)'; }}
                >
                  <Play size={12} />
                  {t('info.start_catchment', { count: selectedCount })}
                </button>
              )}
            </div>
          </>
        )}

        {/* Resize handle */}
        <div className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize" onMouseDown={onResizeStart} style={{ touchAction: 'none' }}>
          <svg width="16" height="16" viewBox="0 0 16 16" className="text-gn-muted">
            <path d="M14 14L8 14L14 8Z" fill="currentColor" opacity="0.4" />
            <path d="M14 14L11 14L14 11Z" fill="currentColor" opacity="0.6" />
          </svg>
        </div>
      </div>
    </>
  );
};
